import {
  Badge,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import { InfoIcon } from "lucide-react";
import type { AttendanceHistoryDTO } from "@/modules/students/domain/repo/list-attendance";
import { formatDate, formatDateTime } from "@/shared/lib/date-time";
import { formatSlotHours } from "@/shared/lib/private-lesson-slot-duration";

type AttendanceRow = AttendanceHistoryDTO;

type AttendanceHistoryTableProps = {
  data: AttendanceRow[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, _limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
};

export function AttendanceHistoryTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
}: AttendanceHistoryTableProps) {
  const columns: TanstackReactTable.ColumnDef<AttendanceRow>[] = [
    createSortableColumn("checkedInAt", "ວັນທີ & ເວລາ", {
      cell: ({ row }) => {
        const isAbsence = row.original.isAbsence;
        const timestamp = isAbsence
          ? row.original.recordedAt ?? row.original.checkedInAt
          : row.original.checkedInAt;
        return (
          <div>
            <div className="font-semibold">{formatDate(timestamp)}</div>
            <div className="text-muted-foreground text-xs">
              {formatDateTime(timestamp)}
            </div>
          </div>
        );
      },
    }),
    {
      header: "ສະຖານະ",
      accessorKey: "sessionType",
      cell: ({ row }) => {
        const isAbsence = row.original.isAbsence;
        if (isAbsence) {
          return <Badge variant="destructive">ຂາດຮຽນ</Badge>;
        }
        const type = row.original.sessionType;
        return (
          <Badge variant="outline">
            {type === "general_class"
              ? "ຄລາດສ໌ທົ່ວໄປ"
              : type === "private_lesson"
                ? "ຮຽນສ່ວນຕົວ"
                : "ເວີກຊອບ"}
          </Badge>
        );
      },
    },
    {
      header: "ຊື່ຄລາດສ໌ / ການຈອງ",
      accessorKey: "className",
      cell: ({ row }) => {
        const isPrivateLesson = row.original.sessionType === "private_lesson";
        const className = row.original.className;
        const privateLessonDate = row.original.privateLessonDate;
        const privateLessonStartTime = row.original.privateLessonStartTime;
        const privateLessonEndTime = row.original.privateLessonEndTime;

        if (isPrivateLesson) {
          return (
            <div>
              {privateLessonDate && (
                <div className="font-semibold">
                  {formatDate(privateLessonDate)}
                </div>
              )}
              {privateLessonStartTime && privateLessonEndTime && (
                <div className="text-muted-foreground text-xs">
                  {privateLessonStartTime} - {privateLessonEndTime}
                </div>
              )}
              {!privateLessonDate && !privateLessonStartTime && "-"}
            </div>
          );
        }

        return (
          <div>
            {className && <div className="font-semibold">{className}</div>}
            {!className && "-"}
          </div>
        );
      },
    },
    {
      header: "ລະດັບ & ຫ້ອງ",
      accessorKey: "levelName",
      cell: ({ row }) => {
        const isPrivateLesson = row.original.sessionType === "private_lesson";
        const levelName = row.original.levelName;
        const roomName = row.original.roomName;
        const privateLessonDurationMinutes =
          row.original.privateLessonDurationMinutes;

        if (isPrivateLesson) {
          if (!roomName && !privateLessonDurationMinutes) return "-";
          return (
            <div>
              {roomName && <div className="font-semibold">{roomName}</div>}
              {privateLessonDurationMinutes && (
                <div className="text-muted-foreground text-xs">
                  {Math.floor(privateLessonDurationMinutes / 60)} ຊົ່ວໂມງ{" "}
                  {privateLessonDurationMinutes % 60 > 0 &&
                    `${privateLessonDurationMinutes % 60} ນາທີ`}
                </div>
              )}
            </div>
          );
        }

        if (!levelName && !roomName) return "-";
        return (
          <div>
            {levelName && <div className="font-semibold">{levelName}</div>}
            {roomName && (
              <div className="text-muted-foreground text-xs">{roomName}</div>
            )}
          </div>
        );
      },
    },
    {
      header: "ຄູສອນ",
      accessorKey: "instructorName",
      cell: ({ row }) => row.original.instructorName || "-",
    },
    {
      header: "ສິດທີ່ໃຊ້",
      accessorKey: "packageName",
      cell: ({ row }) => {
        if (row.original.isAbsence) {
          return <span className="text-muted-foreground">-</span>;
        }
        const packageName = row.original.packageName;
        const privateLessonPackageName = row.original.privateLessonPackageName;
        if (packageName) {
          return (
            <span className="font-semibold text-destructive">
              {packageName}
            </span>
          );
        }
        if (privateLessonPackageName) {
          return (
            <span className="font-semibold text-destructive">
              {privateLessonPackageName}
            </span>
          );
        }
        return "-";
      },
    },
    {
      header: "ຊົ່ວໂມງ",
      accessorKey: "hoursConsumed",
      cell: ({ row }) => {
        const hoursConsumed = row.original.hoursConsumed;
        if (hoursConsumed !== undefined) {
          return `${formatSlotHours(hoursConsumed)} ຊົ່ວໂມງ`;
        }
        return "-";
      },
    },
    {
      header: "ບັນທຶກໂດຍ",
      accessorKey: "checkedInByName",
      cell: ({ row }) => {
        if (row.original.isAbsence) {
          return (
            <span className="text-muted-foreground text-sm">
              {row.original.recordedByName || "-"}
            </span>
          );
        }
        return (
          <span className="text-sm">
            {row.original.checkedInByName || "-"}
          </span>
        );
      },
    },
    {
      header: "Override",
      accessorKey: "hasOverride",
      cell: ({ row }) => {
        if (row.original.isAbsence) {
          return "-";
        }
        if (row.original.hasOverride) {
          return (
            <div className="flex items-center justify-center">
              <div className="flex size-5 items-center justify-center rounded-full bg-destructive/10">
                <InfoIcon className="size-3 text-destructive" />
              </div>
            </div>
          );
        }
        return "-";
      },
    },
  ];

  return (
    <DataTable<AttendanceRow, unknown>
      noDataMessage="ບໍ່ພົບປະຫວັດການເຂົ້າຮຽນ"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (sorting[0]?.id === "") return;
        onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
      }}
    />
  );
}
